import { useRef, useMemo, useState, useEffect, useCallback } from 'react';

import { animationEnd } from 'helpers';
import { screenSize } from 'styles/mixins';

const useLayout = (type, hideDelay, onClose, cantClose) => {
	const [ignoreClose, setIgnoreClose] = useState(
		type === 'confirm' || cantClose,
	);

	const [state, setState] = useState(() => ({
		scrollY: window.scrollY,
		animateEnded: false,
		close: false,
	}));

	const boxRef = useRef();
	const bodyRef = useRef();
	const isDesktop = useRef(null);

	const animationEndName = useMemo(() => animationEnd());

	const fixScroll = useCallback((active) => {
		if (active) {
			document.body.style.position = 'fixed';
			document.body.style.top = `-${state.scrollY}px`;
		} else {
			document.body.style.position = null;
			document.body.style.top = null;
			window.scrollTo(0, state.scrollY);
		}
	}, []);

	const handleResize = useCallback(() => {
		const newIsDesktop = window.innerWidth >= screenSize.tablet;

		if (newIsDesktop && isDesktop.current !== true) {
			isDesktop.current = true;
			calculateHeight();
			fixScroll(false);
		} else if (!newIsDesktop && isDesktop.current !== false) {
			isDesktop.current = false;
			calculateHeight();
			fixScroll(false);
		}
	}, []);

	const calculateHeight = useCallback((height = null) => {
		if (window.innerWidth >= screenSize.tablet) {
			const maxHeight = height || bodyRef.current.offsetHeight;

			if (boxRef.current.style.maxHeight !== maxHeight) {
				boxRef.current.style.maxHeight = `${maxHeight}px`;
			}
		} else {
			boxRef.current.style.maxHeight = null;
		}
	}, []);

	const closeTimer = useRef();
	useEffect(() => {
		if (type === 'alert' && hideDelay !== 0) {
			closeTimer.current = setTimeout(() => {
				closeHandler();
			}, hideDelay);
		}

		handleResize();
		window.addEventListener('resize', handleResize);

		// start animation on Open:
		const windowWidth = window.innerWidth;
		const scrollWidth = `${
			windowWidth - document.documentElement.clientWidth
		}px`;

		document.body.style.paddingRight = scrollWidth;
		document.body.style.overflowY = 'hidden';

		if (windowWidth <= screenSize.tablet) {
			const onStartAnimationEnded = () => {
				// setAnimateEnded(true);
				if (!state.close) {
					setState({ ...state, animateEnded: true });

					// TODO: (hamid) prevent position:fixed when modal content is less than window height
					// const positionInfo = boxRef.current.getBoundingClientRect();
					// const height = positionInfo.height;
					// const width = positionInfo.width;
					// console.log(height, width);

					boxRef.current.removeEventListener(
						animationEndName,
						onStartAnimationEnded,
					);

					fixScroll(true);
				}
			};
			if (document.getElementById('header')) {
				document.getElementById('header').style.paddingRight = scrollWidth;
			}
			boxRef.current.addEventListener(animationEndName, onStartAnimationEnded, {
				once: true,
			});
		}

		return () => {
			clearTimeout(closeTimer.current);
			window.removeEventListener('resize', handleResize);

			if (!state.close) {
				fixScroll(false);
				document.body.style.overflowY = 'auto';
				document.body.style.paddingRight = 0;
				if (document.getElementById('header')) {
					document.getElementById('header').style.paddingRight = 0;
				}
			}
		};
	}, []);

	const closeHandler = useCallback(
		(force = false) => {
			if (!ignoreClose || force) {
				setState((s) => ({ ...s, animateEnded: false, close: true }));
				fixScroll(false);

				const onEnd = () => {
					if (!boxRef.current) return;

					boxRef.current.removeEventListener(animationEndName, onEnd);
					document.body.style.overflowY = 'auto';
					document.body.style.paddingRight = 0;

					onClose();

					if (document.getElementById('header')) {
						document.getElementById('header').style.paddingRight = 0;
					}
				};
				if (animationEndName) {
					boxRef.current.addEventListener(animationEndName, onEnd, {
						once: true,
					});
				} else {
					onEnd();
				}
			}
		},
		[ignoreClose],
	);

	const onLightBoxClick = useCallback(
		(e) => {
			if (e.currentTarget === e.target) {
				closeHandler();
			}
		},
		[closeHandler, ignoreClose],
	);

	return {
		...state,
		onLightBoxClick,
		setIgnoreClose,
		boxRef,
		bodyRef,
		closeHandler,
		calculateHeight,
		ignoreClose,
	};
};

export default useLayout;
